import * as THREE from "three";

// ---------------------------------------------------------------------------
// Timeline: minutes measured from 22:00 (10:00 PM) EDT. 0..300 => 10PM..3AM.
// Maximum totality of the 2026 Deep Lunar Eclipse ~ 00:12 AM => t = 132 min.
// ---------------------------------------------------------------------------
export const TIMELINE = { start: 0, end: 300, max: 132 };

export const EVENTS = [
  { min: 0, label: "Penumbral First Contact", short: "P1" },
  { min: 60, label: "Umbral Ingress", short: "U1" },
  { min: 132, label: "Maximum Totality", short: "MAX" },
  { min: 204, label: "Umbral Egress", short: "U4" },
  { min: 300, label: "Penumbral End", short: "P4" },
];

export const PHASES = [
  { name: "Penumbral Entrance", range: [0, 60] },
  { name: "Umbral Ingress", range: [60, 110] },
  { name: "Deep Umbral / Totality", range: [110, 154] },
  { name: "Umbral Egress", range: [154, 240] },
  { name: "Recovery", range: [240, 300] },
];

// Scene geometry constants (1 scene unit = 1 lunar radius) ------------------
const U = 1.75;   // umbral radius in lunar radii
const PEN = 3.0;  // penumbral outer radius
export const SUN_POS = new THREE.Vector3(-26, -11, 0);
export const EARTH_POS = new THREE.Vector3(0, 0, 0);
export const MOON_DIST = 17;

const shadowDir = new THREE.Vector3().subVectors(EARTH_POS, SUN_POS).normalize();
const _up = new THREE.Vector3(0, 1, 0);
const rightDir = new THREE.Vector3().crossVectors(shadowDir, _up).normalize();
const planeUp = new THREE.Vector3().crossVectors(rightDir, shadowDir).normalize();
const AXIS_POINT = new THREE.Vector3().copy(EARTH_POS).addScaledVector(shadowDir, MOON_DIST);

export const SHADOW = { dir: shadowDir, umbra: U, penumbra: PEN, axisPoint: AXIS_POINT };

// ---------------------------------------------------------------------------
// Moon orbital mechanics — a true circular orbit parented to the Earth's centre.
// theta = 0 places the Moon at the anti-solar point (deepest shadow / totality).
// The orbit plane is inclined so the closest approach to the shadow axis equals
// the preset impact parameter b0 (grazing => partial eclipses).
// ---------------------------------------------------------------------------
export const MOON_R = 0.9;                 // Moon scene radius (units per lunar radius)
export const ORBIT_SWEEP = Math.PI * 1.2;  // angular arc mapped across the 10PM–3AM timeline

export function moonOrbitPoint(theta, b0 = 0) {
  const incl = Math.asin(Math.min(0.85, (b0 * MOON_R) / MOON_DIST));
  const axisDir = shadowDir.clone().multiplyScalar(Math.cos(incl)).addScaledVector(planeUp, Math.sin(incl));
  return EARTH_POS.clone()
    .addScaledVector(axisDir, MOON_DIST * Math.cos(theta))
    .addScaledVector(rightDir, MOON_DIST * Math.sin(theta));
}

// ---------------------------------------------------------------------------
// Circle-overlap coverage math
// ---------------------------------------------------------------------------
function lensArea(R, r, d) {
  if (d >= R + r) return 0;
  if (d <= Math.abs(R - r)) return Math.PI * Math.min(R, r) ** 2;
  const R2 = R * R, r2 = r * r;
  const a = Math.acos((d * d + R2 - r2) / (2 * d * R));
  const b = Math.acos((d * d + r2 - R2) / (2 * d * r));
  return R2 * (a - 0.5 * Math.sin(2 * a)) + r2 * (b - 0.5 * Math.sin(2 * b));
}
export function coverageAtSep(sep) {
  return Math.min(1, lensArea(U, 1, sep) / Math.PI);
}
// invert: find separation giving a target peak coverage
export function sepForCoverage(cov) {
  if (cov >= 0.999) return 0.0;
  if (cov <= 0.001) return PEN - 0.05; // grazing / penumbral
  let lo = 0, hi = U + 1;
  for (let i = 0; i < 40; i++) {
    const mid = (lo + hi) / 2;
    if (coverageAtSep(mid) > cov) lo = mid; else hi = mid;
  }
  return (lo + hi) / 2;
}

// ---------------------------------------------------------------------------
// Danjon Scale L=0..4  ->  copper color + shadow luminance
// ---------------------------------------------------------------------------
const DANJON_STOPS = [
  { l: 0, c: [0.09, 0.05, 0.03], lum: 0.015 }, // near-black
  { l: 1, c: [0.33, 0.11, 0.06], lum: 0.05 },  // dark brown/grey
  { l: 2, c: [0.55, 0.17, 0.08], lum: 0.09 },  // deep rust red
  { l: 3, c: [0.82, 0.30, 0.12], lum: 0.15 },  // brick / copper
  { l: 4, c: [1.0, 0.48, 0.24], lum: 0.24 },   // bright coppery orange
];
export function danjonColor(L) {
  L = Math.max(0, Math.min(4, L));
  const i = Math.min(3, Math.floor(L));
  const a = DANJON_STOPS[i], b = DANJON_STOPS[i + 1];
  const t = L - i;
  const c = [0, 1, 2].map((k) => a.c[k] + (b.c[k] - a.c[k]) * t);
  const lum = a.lum + (b.lum - a.lum) * t;
  return { rgb: c, lum };
}

// ---------------------------------------------------------------------------
// Historical eclipse presets (from the reference diagram)
// peak = peak umbral coverage fraction; danjon = characteristic L
// ---------------------------------------------------------------------------
const RAW_PRESETS = [
  { id: "e2026", date: "08/28/2026", name: "2026 Deep Eclipse", type: "Total", peak: 0.962, danjon: 2.6 },
  { id: "e20070828", date: "08/28/2007", name: "Total (Central)", type: "Total", peak: 1.0, danjon: 3.2 },
  { id: "e20190121", date: "01/21/2019", name: "Super Blood Wolf", type: "Total", peak: 1.0, danjon: 3.0 },
  { id: "e20221108", date: "11/08/2022", name: "Beaver Blood Moon", type: "Total", peak: 1.0, danjon: 3.4 },
  { id: "e20250314", date: "03/14/2025", name: "Blood Worm Moon", type: "Total", peak: 1.0, danjon: 3.1 },
  { id: "e20101221", date: "12/21/2010", name: "Solstice Total", type: "Total", peak: 1.0, danjon: 3.0 },
  { id: "e20141008", date: "10/08/2014", name: "Hunter Total", type: "Total", peak: 1.0, danjon: 2.6 },
  { id: "e20080220", date: "02/20/2008", name: "Snow Total", type: "Total", peak: 1.0, danjon: 2.6 },
  { id: "e20211119", date: "11/19/2021", name: "Near-Total Partial", type: "Partial", peak: 0.974, danjon: 2.1 },
  { id: "e20031108", date: "11/08/2003", name: "Shallow Total", type: "Total", peak: 1.0, danjon: 1.6 },
  { id: "e20170807", date: "08/07/2017", name: "Partial", type: "Partial", peak: 0.25, danjon: 1.2 },
  { id: "e20060314", date: "03/14/2006", name: "Penumbral", type: "Penumbral", peak: 0.02, danjon: 0.3 },
  { id: "e20170211", date: "02/11/2017", name: "Penumbral", type: "Penumbral", peak: 0.02, danjon: 0.3 },
];
export const PRESETS = RAW_PRESETS.map((p) => ({ ...p, b0: sepForCoverage(p.peak) }));

// ---------------------------------------------------------------------------
// Clock formatting
// ---------------------------------------------------------------------------
export function formatClock(min) {
  const total = 22 * 60 + min;
  const h24 = Math.floor(total / 60) % 24;
  const m = Math.round(total % 60) % 60;
  const period = h24 >= 12 ? "PM" : "AM";
  let h = h24 % 12;
  if (h === 0) h = 12;
  return `${h}:${String(m).padStart(2, "0")} ${period}`;
}

export function phaseName(min) {
  for (const p of PHASES) if (min >= p.range[0] && min <= p.range[1]) return p.name;
  return "Penumbral";
}

// ---------------------------------------------------------------------------
// Master state solver
// ---------------------------------------------------------------------------
export function computeSim(timeMin, danjon, bortle, b0) {
  // Moon position on its circular orbit around the Earth's centre.
  const theta = ((timeMin - TIMELINE.max) / TIMELINE.end) * ORBIT_SWEEP;
  const moonPos = moonOrbitPoint(theta, b0);

  // Eclipse geometry derived from the Moon's true orbital position: its
  // perpendicular distance from the Sun→Earth shadow axis.
  const vec = moonPos.clone().sub(EARTH_POS);
  const alongAxis = vec.dot(shadowDir);
  const perpVec = vec.clone().addScaledVector(shadowDir, -alongAxis);
  const sepUnits = perpVec.length();
  const sep = sepUnits / MOON_R;                          // in lunar radii
  const coverage = alongAxis > 0 ? coverageAtSep(sep) : 0; // only on the anti-solar side

  // umbra centre projected onto the Moon disc (magnitude = sep, in lunar radii)
  const umbraCenter = perpVec.clone().multiplyScalar(-1 / MOON_R);

  const dj = danjonColor(danjon);
  const brightness = (1 - coverage) + coverage * dj.lum; // luminous fraction of disc
  const penumbral = sep > U && sep < PEN ? THREE.MathUtils.clamp((PEN - sep) / (PEN - U), 0, 1) : (sep <= U ? 1 : 0);

  // biometrics -------------------------------------------------------------
  const sens = bortle <= 3 ? 1.0 : 0.5; // rural dark sky => heightened sensitivity
  const melatonin = 45 + 35 * (timeMin / 300) - 22 * brightness * sens;
  const delta = 100 - 32 * brightness * sens;
  const sleepOnset = 18 + 60 * brightness * sens;

  // radiometric ------------------------------------------------------------
  const surfaceTemp = 120 - 250 * coverage; // deg C, sunlit vs shadowed regolith
  const orbitalVelocity = 1.018 + 0.004 * Math.cos((timeMin / 300) * Math.PI);
  const umbralDepth = coverage * 100;

  return {
    timeMin, danjon, bortle, b0,
    clock: formatClock(timeMin),
    phase: phaseName(timeMin),
    coverage,
    brightness,
    penumbral,
    moonPos,
    umbraCenter,
    danjonRgb: dj.rgb,
    danjonLum: dj.lum,
    melatonin: Math.max(8, melatonin),
    delta: Math.max(50, Math.min(100, delta)),
    sleepOnset,
    surfaceTemp,
    orbitalVelocity,
    umbralDepth,
  };
}

// biometric series for charts (sampled across the whole timeline)
export function biometricSeries(danjon, bortle, b0) {
  const out = [];
  for (let t = 0; t <= 300; t += 6) {
    const s = computeSim(t, danjon, bortle, b0);
    out.push({
      t,
      clock: formatClock(t),
      melatonin: +s.melatonin.toFixed(1),
      delta: +s.delta.toFixed(1),
      onset: +s.sleepOnset.toFixed(1),
    });
  }
  return out;
}
